import { isValidCountryCode } from 'src/utils/country';
import {
  BuyerWithSalesDto,
  CreateRetailSaleGroupInput,
  ListingDetails,
  MarketplacePaymentLineType,
  MarketplacePaymentState,
  PointOfSaleSaleStatus,
  SaleLineItemCoverage,
  Ticket,
  TicketType,
} from 'src/WebApiController';

import {
  MultiListingSelectionRow,
  RetailSaleFormValues,
  RetailSalePaymentLine,
} from './types';

const roundTo2 = (n: number) => Math.round(n * 100) / 100;

/**
 * Taxable base for the auto-generated buyer tax line (POS-8561): the total net proceeds plus the
 * net of the manual charges/credits. The managed Tax line itself is excluded so the calculation is
 * idempotent when the recomputed line is written back into `marketplacePaymentLines`.
 */
export const getRetailTaxableAmount = (
  totalNetProceeds: number | null | undefined,
  marketplacePaymentLines: RetailSalePaymentLine[] | null | undefined
): number => {
  const manualTotal = (marketplacePaymentLines ?? [])
    .filter((l) => l.paymentLineType !== MarketplacePaymentLineType.Tax)
    .reduce((sum, l) => sum + (l.amount ?? 0), 0);
  return (totalNetProceeds ?? 0) + manualTotal;
};

/** Tax amount = rate (a decimal fraction, e.g. 0.0825) × taxable base, rounded to cents. */
export const computeRetailTaxAmount = (
  taxRate: number,
  taxableAmount: number
): number => roundTo2(taxRate * taxableAmount);

export const getRetailExternalSaleId = (listingId: number) => {
  return `Retail-${listingId}-${Date.now()}`;
};

export const getRetailMerchantReferenceId = (saleId: number) => {
  return `Retail-${saleId}-${Date.now()}`;
};

/** Inclusive seat-range check; numeric when both bounds and the seat are numeric, else lexicographic. */
const isSeatInRange = (
  seat: string | null | undefined,
  from: string | null | undefined,
  to: string | null | undefined
): boolean => {
  if (!from && !to) return true;
  const s = (seat ?? '').trim();
  const asNum = (v: string) => (/^\d+$/.test(v) ? Number(v) : null);
  const sn = asNum(s);
  const fn = from ? asNum(from) : null;
  const tn = to ? asNum(to) : null;

  // Numeric comparison when everything present is numeric.
  if (sn != null && (!from || fn != null) && (!to || tn != null)) {
    if (fn != null && sn < fn) return false;
    if (tn != null && sn > tn) return false;
    return true;
  }

  // Fallback to lexicographic comparison.
  if (from && s < from) return false;
  if (to && s > to) return false;
  return true;
};

/**
 * Infers the ticket ids to sell from the form's section/row/seat selection: the listing's available
 * tickets in the selected row whose seat falls within [seatFrom, seatTo]. Falls back to the first
 * quantitySold available tickets when no seat range is given (e.g. GA). Available = not sold, held,
 * spoiled, or wasted.
 */
export const resolveSaleTicketIds = (
  listing: ListingDetails | undefined,
  values: RetailSaleFormValues
): number[] => {
  const available = (listing?.tickets ?? []).filter(
    (t: Ticket) =>
      t.saleId == null &&
      t.holdId == null &&
      t.spoiledSaleId == null &&
      !t.isWasted
  );

  let matched = available;
  if (values.row) {
    matched = matched.filter((t) => (t.row ?? '') === values.row);
  }
  if (values.seatFrom || values.seatTo) {
    matched = matched.filter((t) =>
      isSeatInRange(t.seat, values.seatFrom, values.seatTo)
    );
  }

  const ids = matched.map((t) => t.id);
  const quantity = values.quantitySold ?? ids.length;
  return ids.slice(0, quantity);
};

/**
 * Multi-listing mode (POS-8649): derive the flat TicketIds set from the
 * per-row `availableTicketIds.slice(0, qtyToSell)` unions. Rows with qty=0
 * contribute nothing; row order is preserved so downstream diagnostics can
 * correlate ticket IDs back to their source row.
 *
 * The result is deduplicated because the backend contract is `ISet<long>` —
 * `SaleClient.createRetailSaleGroup` rejects duplicate ticket IDs with an
 * "item with the same key has already been added" error. Overlaps can leak
 * in when upstream data has anomalies (e.g. the same listing ID resolved
 * through two code paths); dedup here keeps the flow resilient to that.
 */
export const deriveTicketIdsFromSelections = (
  selections: MultiListingSelectionRow[] | null | undefined
): number[] => {
  if (!selections || selections.length === 0) return [];
  const flat = selections.flatMap((sel) =>
    sel.qtyToSell > 0 ? sel.availableTicketIds.slice(0, sel.qtyToSell) : []
  );
  return Array.from(new Set(flat));
};

/**
 * Builds the CreateRetailSaleGroupInput for the retail-sale flow. Every retail sale is created as a
 * sales group (a single sale is a group of one); the tickets to sell are inferred from the form's
 * section/row/seat selection via {@link resolveSaleTicketIds}.
 *
 * Multi-listing mode (POS-8649): when `values.perListingSelections` is present, ticket IDs are
 * derived from it via {@link deriveTicketIdsFromSelections} instead of the single-listing
 * section/row/seatFrom/seatTo/quantitySold form fields (which are unset in that mode).
 */
export const buildRetailSaleGroupInput = (
  values: RetailSaleFormValues,
  listing: ListingDetails | undefined,
  selectedBuyer: BuyerWithSalesDto | undefined,
  includeExternalFields = true
): CreateRetailSaleGroupInput => {
  // POS-8726: 'credit' reuses the external payment path with CustomerCredit method type
  const isExternal =
    values.paymentMethodChoice === 'external' ||
    values.paymentMethodChoice === 'credit';
  // POS-8726: For credit payments, compute the amount at submission time from the
  // current form values so it reflects any quantity/listing changes made after
  // selecting the payment method. External payments use the user-entered amount.
  const paymentAmount =
    values.paymentMethodChoice === 'credit'
      ? getPaymentAmount(values)
      : (values.externalPaymentAmount ?? null);
  const externalFields =
    isExternal && includeExternalFields
      ? {
          externalPaymentMethodType: values.externalPaymentMethodType ?? null,
          externalPaymentNote: values.externalPaymentNote ?? null,
          externalPaymentAmount: paymentAmount,
          externalPaymentDate: values.externalPaymentDate ?? null,
        }
      : {
          externalPaymentMethodType: null,
          externalPaymentNote: null,
          externalPaymentAmount: null,
          externalPaymentDate: null,
        };

  const perListingSelections = values.perListingSelections;
  const isMultiListing =
    perListingSelections != null && perListingSelections.length > 0;

  const ticketIds = isMultiListing
    ? deriveTicketIdsFromSelections(perListingSelections)
    : resolveSaleTicketIds(listing, values);

  return {
    ticketIds,
    salesGroupName: null,
    saleDate: values.saleDate ?? null,
    currencyCode: values.currencyCode ?? null,
    inHandDate: values.inHandDate ?? null,
    ticketType: (values.ticketType as TicketType) ?? null,
    buyerName: selectedBuyer?.buyerName ?? null,
    buyerEmail: selectedBuyer?.buyerEmail ?? null,
    buyerPhone: selectedBuyer?.buyerPhoneNumber ?? null,
    internalNotes: values.privateNotes ?? null,
    saleStatus: PointOfSaleSaleStatus.PendingFulfillment,
    totalNetProceeds: values.totalNetProceeds ?? null,
    lineItems:
      values.marketplacePaymentLines
        ?.filter((line) => line.amount !== 0)
        .map((line) => ({
          paymentLineType: line.paymentLineType,
          amount: line.amount,
          ratePercent: line.ratePercent ?? null,
          reference: line.externalPaymentId || null,
          description: line.description || null,
          saleLineItemChargeReason: line.saleLineItemChargeReason ?? null,
          saleLineItemCreditReason: line.saleLineItemCreditReason ?? null,
        })) ?? null,
    ...externalFields,
  };
};

export const getPaymentAmount = (values: RetailSaleFormValues) => {
  const { totalNetProceeds, marketplacePaymentLines } = values;
  const marketplaceLinesCost = (marketplacePaymentLines ?? []).reduce(
    (ret, line) => {
      ret += line.amount ?? 0;
      return ret;
    },
    0
  );
  return (totalNetProceeds ?? 0) + marketplaceLinesCost;
};

export const getPaymentCountryCode = (
  selectedBuyer: BuyerWithSalesDto | undefined,
  knownCountries?: Record<string, string> | null
) => {
  const nameToCountryCode = Object.entries(knownCountries ?? {}).reduce(
    (acc, [code, name]) => {
      acc[name] = code;
      return acc;
    },
    {} as Record<string, string>
  );
  const contryName =
    selectedBuyer?.billingCountry ?? selectedBuyer?.shippingCountry;
  if (contryName) {
    // incase the country names are set as code
    if (isValidCountryCode(contryName)) {
      return contryName;
    }
    if (nameToCountryCode[contryName]) {
      const countryCode = nameToCountryCode[contryName];
      if (isValidCountryCode(countryCode)) {
        return countryCode;
      }
    }
  }

  return 'US';
};

/**
 * Proportionally distributes a partial payment amount across all unpaid
 * marketplace payment lines. Each line receives an amount proportional to
 * its share of the total unpaid balance. Rounding is handled per-line with
 * the remainder absorbed by the last line so the sum equals `partialAmount`
 * exactly.
 *
 * @param partialAmount  The total partial payment amount to distribute.
 * @param salesData      Array of sale DTOs (from useGetSalesInSalesGroup)
 *                       whose `mkpLines` will be filtered for unpaid lines.
 * @returns              SaleLineItemCoverage[] ready to send to the API.
 */
export const computeProportionalCoverages = (
  partialAmount: number,
  salesData: {
    mkpLines?:
      | { id: number; state: MarketplacePaymentState; amt: { amt: number } }[]
      | null;
  }[]
): SaleLineItemCoverage[] => {
  const unpaidLines = salesData
    .flatMap((sale) => sale.mkpLines ?? [])
    .filter(
      (line) =>
        line.state === MarketplacePaymentState.Unpaid && line.amt.amt > 0
    );

  if (unpaidLines.length === 0) return [];

  const totalUnpaid = unpaidLines.reduce((sum, l) => sum + l.amt.amt, 0);
  if (totalUnpaid <= 0) return [];

  const coverages: SaleLineItemCoverage[] = [];
  let allocated = 0;

  for (let i = 0; i < unpaidLines.length; i++) {
    const line = unpaidLines[i];
    const isLast = i === unpaidLines.length - 1;

    if (isLast) {
      // Last line absorbs any rounding remainder, capped at the line's amount
      const remainder = roundTo2(partialAmount - allocated);
      const capped = Math.min(remainder, line.amt.amt);
      if (capped > 0) {
        coverages.push({
          marketplacePaymentLineId: line.id,
          amountToPay: capped,
        });
      }
    } else {
      const proportion = line.amt.amt / totalUnpaid;
      // Cap at the line's amount so rounding never exceeds what's owed
      const amount = Math.min(
        roundTo2(partialAmount * proportion),
        line.amt.amt
      );
      allocated += amount;
      if (amount > 0) {
        coverages.push({
          marketplacePaymentLineId: line.id,
          amountToPay: amount,
        });
      }
    }
  }

  return coverages;
};
